import { supabase } from '@/lib/supabase';
import { Organization, OrganizationMember } from '@/types/api/organization';

function mapOrg(row: any): Organization {
  return {
    id: row.id,
    name: row.name,
    slug: row.slug,
    logoUrl: row.logo_url,
    createdBy: row.created_by,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function mapMember(row: any): OrganizationMember {
  return {
    id: row.id,
    organizationId: row.organization_id,
    userId: row.user_id,
    role: row.role,
    createdAt: row.created_at,
  };
}

const organizationsService = {
  async getMyOrganizations(): Promise<Organization[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    const { data: memberships, error: memErr } = await supabase
      .from('organization_members')
      .select('organization_id')
      .eq('user_id', user.id);

    if (memErr || !memberships || memberships.length === 0) return [];

    const orgIds = memberships.map((m) => m.organization_id);
    const { data, error } = await supabase
      .from('organizations')
      .select('*')
      .in('id', orgIds)
      .order('name', { ascending: true });
    if (error) throw error;
    return (data ?? []).map(mapOrg);
  },

  async getById(id: string): Promise<Organization> {
    const { data, error } = await supabase
      .from('organizations')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return mapOrg(data);
  },

  async create(payload: { name: string; slug: string }): Promise<Organization> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    // Create organization
    const { data: org, error: orgErr } = await supabase
      .from('organizations')
      .insert({
        name: payload.name,
        slug: payload.slug.toLowerCase().replace(/[^a-z0-9-]/g, ''),
        created_by: user.id,
      })
      .select()
      .single();

    if (orgErr) throw orgErr;

    // Add creator as owner
    await supabase
      .from('organization_members')
      .insert({
        organization_id: org.id,
        user_id: user.id,
        role: 'owner',
      });

    return mapOrg(org);
  },

  async update(id: string, payload: { name?: string; slug?: string; logoUrl?: string }): Promise<Organization> {
    const updates: Record<string, unknown> = {};
    if (payload.name !== undefined) updates.name = payload.name;
    if (payload.slug !== undefined) updates.slug = payload.slug.toLowerCase().replace(/[^a-z0-9-]/g, '');
    if (payload.logoUrl !== undefined) updates.logo_url = payload.logoUrl;

    const { data, error } = await supabase
      .from('organizations')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return mapOrg(data);
  },

  async getMembers(organizationId: string): Promise<OrganizationMember[]> {
    const { data, error } = await supabase
      .from('organization_members')
      .select('*')
      .eq('organization_id', organizationId)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return (data ?? []).map(mapMember);
  },

  async checkSlugAvailable(slug: string): Promise<boolean> {
    const { data } = await supabase
      .from('organizations')
      .select('id')
      .eq('slug', slug.toLowerCase())
      .limit(1);

    return !data || data.length === 0;
  },
};

export default organizationsService;
